import { Typography } from '@scale/scaleui';
import { InteractionStepTaskComponent } from '../../../InteractionStep';
import { Alert } from '../../../TempComponents';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import { useCallback, useEffect, useMemo, useState, useContext, memo } from 'react';
import {
  InteractionStepType,
  DEFAULT_TEXTCOLLECTION_SUBTITLE,
  DEFAULT_TEXTCOLLECTION_TITLE,
  TextCollectionPerResponseStepResponse,
  UIGenerationMetadata,
} from '@scale/shared/types/chat';
import _ from 'lodash';
import { CandidateMessageWithId } from '@scale/shared/types/chat-completion';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import CandidateTextCollectionCard from './CandidateTextCollectionCard';
import { areTextCollectionResponsesConsistent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import {
  useStepParams,
  useStepParamsOrUndefined,
  useStepResponse,
  useMostRecentResponse,
  useMostRecentPrompt,
  useMostRecentResponseForStepType,
  useChatStoreExtract,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useStepSubmitHandler } from '../../../hooks/useStepSubmitHandler';
import { StepWidthWrapper, StepWidth } from '../../../step/StepWidthWrapper';
import TextCollectionPerResponseTabsWrapper, {
  TextCollectionPerResponseTab,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/TextCollectionPerResponse/Components/TextCollectionPerResponseTabsWrapper';
import TwoTextSxSDiffView from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/TwoTextSxSDiffView';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { StepCard } from '../../../step/StepCard';
import { CommentButton } from '../../../feedback/CommentButton';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { twMerge } from 'tailwind-merge';
import {
  RubricField,
  TextCollectionFieldType,
  NumberField,
} from '@scale/shared/types/params/textcollection';
import RubricCriteriasFieldSection from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/fields/RubricCriteriasFieldSection';
import produce from 'immer';
import { areAllAnnotationsComplete } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/TextCollectionPerResponse/Components/utils';
import RubricSummaryTable from './RubricSummaryTable';
import { PinButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/PinButton';

const TextCollectionPerResponseTaskComponent: InteractionStepTaskComponent = ({
  readOnly,
  responseIndex,
  isPinnedView,
  complete,
}) => {
  const taskContext = useContext(ChatTaskContext);

  // Feature flags
  const enableIndividualModelResponseReroll = useUnleashFeatureFlag(
    'chat-enable-tcpr-individual-model-response-reroll',
    { projectId: taskContext.projectId },
    false,
  );

  const response = useStepResponse<TextCollectionPerResponseStepResponse>(responseIndex);
  const step = useStepParams<InteractionStepType.TextCollectionPerResponse>(responseIndex);
  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });

  const [error, setError] = useState<string>('');
  const lastPrompt = useMostRecentPrompt(responseIndex);
  const lastResponse = useMostRecentResponse(responseIndex);

  const previousModelResponseSelectorResponse = useMostRecentResponseForStepType(
    InteractionStepType.ModelResponseSelector,
    responseIndex,
  )!;

  const modelResponseSelectorStepId = previousModelResponseSelectorResponse?.step_id;
  const modelResponseSelectorStepParams =
    useStepParamsOrUndefined<InteractionStepType.ModelResponseSelector>(
      previousModelResponseSelectorResponse?.index,
    );
  const isSkipSelectionEnabled = modelResponseSelectorStepParams?.params.skip_selection ?? false;

  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );
  const markStepAsCompletedByIndex = useChatStoreExtract(state => state.markStepAsCompletedByIndex);

  const candidates: CandidateMessageWithId[] = useMemo(() => {
    if (!previousModelResponseSelectorResponse) {
      return [];
    }
    const displayOrder = previousModelResponseSelectorResponse.context.displayOrder as number[];

    return _.compact(
      displayOrder.map(i => previousModelResponseSelectorResponse.context.candidates[i]),
    );
  }, [previousModelResponseSelectorResponse]);

  const defaultResponse = useMemo(() => {
    const response: Record<string, TextCollectionResponse> = {};
    candidates.forEach(candidate => {
      response[candidate.id] = { annotations: {} };
    });

    return response;
  }, [candidates]);

  const [allResponses, setAllResponses] = useState<Record<string, TextCollectionResponse>>(
    response?.context.annotations || defaultResponse,
  );

  const defaultCompletedCandidates: Record<string, boolean> = useMemo(() => {
    const completedCandidates: Record<string, boolean> = {};
    candidates.forEach(candidate => {
      completedCandidates[candidate.id] = true;
    });

    return completedCandidates;
  }, [candidates]);

  const [completedCandidates, setCompletedCandidates] = useState<Record<string, boolean>>(
    defaultCompletedCandidates,
  );
  const setCandidateCompletedState = useCallback(
    (candidateId: string, completed: boolean) => {
      setCompletedCandidates(
        produce(completedCandidates, draft => {
          draft[candidateId] = completed;
        }),
      );
    },
    [completedCandidates],
  );

  const isEveryCandidateCompleted = useMemo(() => {
    return _.every(completedCandidates, Boolean);
  }, [completedCandidates]);

  const rubric: RubricField | undefined = step.params?.fields?.find(
    field => field.type === TextCollectionFieldType.Rubric,
  ) as RubricField | undefined;

  const responseScoreField = step.params?.fields?.find(
    field => field.type === TextCollectionFieldType.Number && !!field.use_as_rubric_response_score,
  ) as NumberField | undefined;

  const [doesRubricNeedSaving, setDoesRubricNeedSaving] = useState<boolean>(false);
  const [doesReactUIGeneratedUrlNeedSaving, setDoesReactUIGeneratedUrlNeedSaving] =
    useState<boolean>(false);

  /*
   * Sync the completed state of the step with the completed state of the candidates
   * Only relevant if the step has already been submitted at least once
   */
  useEffect(() => {
    if (!response) {
      return;
    }

    if (complete === isEveryCandidateCompleted) {
      return;
    }
    if (isEveryCandidateCompleted && !doesRubricNeedSaving && !doesReactUIGeneratedUrlNeedSaving) {
      markStepAsCompletedByIndex(responseIndex);
      setError('');
    } else {
      markStepAsIncompletedByIndex(responseIndex);
    }
  }, [
    response,
    complete,
    isEveryCandidateCompleted,
    markStepAsCompletedByIndex,
    markStepAsIncompletedByIndex,
    responseIndex,
    doesRubricNeedSaving,
    doesReactUIGeneratedUrlNeedSaving,
  ]);

  const [reactUIGeneratedUrlWithMetadataByModel, setReactUIGeneratedUrlWithMetadataByModel] =
    useState<
      Record<
        string,
        {
          reactUIGeneratedUrl?: string;
        } & UIGenerationMetadata
      >
    >(() => {
      return response?.context?.reactUIGeneratedUrlByModel || {};
    });
  const [
    savedReactUIGeneratedUrlWithMetadataByModel,
    setSavedReactUIGeneratedUrlWithMetadataByModel,
  ] = useState<
    Record<
      string,
      {
        reactUIGeneratedUrl?: string;
      } & UIGenerationMetadata
    >
  >(() => {
    return response?.context?.reactUIGeneratedUrlByModel || {};
  });

  const handleUpdateReactGeneratedUrlWithMetadataByModel = useCallback(
    (
      candidateId: string,
      reactGeneratedUrlWithMetadata: {
        reactUIGeneratedUrl?: string;
      } & UIGenerationMetadata,
    ) => {
      const newReactUIGeneratedUrlWithMetadataByModel = {
        ...reactUIGeneratedUrlWithMetadataByModel,
        [candidateId]: reactGeneratedUrlWithMetadata,
      };
      setReactUIGeneratedUrlWithMetadataByModel(newReactUIGeneratedUrlWithMetadataByModel);
    },
    [reactUIGeneratedUrlWithMetadataByModel],
  );

  useEffect(() => {
    if (
      !_.isEqual(
        reactUIGeneratedUrlWithMetadataByModel,
        response?.context?.reactUIGeneratedUrlByModel,
      )
    ) {
      setDoesReactUIGeneratedUrlNeedSaving(true);
    } else {
      setDoesReactUIGeneratedUrlNeedSaving(false);
    }
  }, [reactUIGeneratedUrlWithMetadataByModel, response]);

  const handleSubmit = useCallback(
    async (newResponses: Record<string, TextCollectionResponse>): Promise<boolean> => {
      const selectedId = previousModelResponseSelectorResponse?.context.selectedId;
      const { fieldToCheck, fields } = step.params;

      // Run any consistency checks before submitting
      if (fieldToCheck && selectedId) {
        const { isValid, errorMessage } = areTextCollectionResponsesConsistent(
          newResponses,
          fields,
          fieldToCheck,
          selectedId,
        );

        if (!isValid) {
          setError(errorMessage);
          return false;
        } else {
          setError('');
        }
      }

      // Only submit if the annotations have changed
      if (
        !_.isEqual(newResponses, response?.context?.annotations) ||
        !_.isEqual(
          reactUIGeneratedUrlWithMetadataByModel,
          response?.context?.reactUIGeneratedUrlByModel,
        )
      ) {
        return await onSubmit(
          {
            context: {
              candidates: previousModelResponseSelectorResponse.context.candidates,
              annotations: newResponses,
              selectedId: previousModelResponseSelectorResponse.context.selectedId,
              reactUIGeneratedUrlByModel: reactUIGeneratedUrlWithMetadataByModel,
            },
            type: InteractionStepType.TextCollectionPerResponse,
            output: lastResponse,
          },
          responseIndex,
          step,
        );
      }

      return true;
    },
    [
      previousModelResponseSelectorResponse,
      lastResponse,
      onSubmit,
      responseIndex,
      step,
      response,
      reactUIGeneratedUrlWithMetadataByModel,
    ],
  );

  // Diff is only supported for exactly two candidates
  const enableDiffView = useUnleashFeatureFlag(
    'enable-tc-per-response-diff-view',
    { projectId: taskContext.projectId },
    false,
  );
  const showTwoCandidateDiffResult = candidates.length === 2 && !!enableDiffView;

  return (
    <StepWidthWrapper
      width={
        isPinnedView
          ? StepWidth.Full
          : !taskContext.enableUnifiedFeedback
          ? StepWidth.Fit
          : StepWidth.Responsive
      }
    >
      <TextCollectionPerResponseTabsWrapper
        className={twMerge(
          'flex flex-col gap-2',
          !taskContext.enableUnifiedFeedback ? 'w-full' : '',
        )}
      >
        {({ tab, setTab }) => (
          <>
            <StepCard className="pb-2" readOnly={readOnly}>
              <StepCard.Header>
                <StepCard.HeaderLeft>
                  <StepCard.Icon icon="question-circle" />
                  <StepCard.HeaderColumn>
                    <StepCard.Title>{step.title || DEFAULT_TEXTCOLLECTION_TITLE}</StepCard.Title>
                    <StepCard.Subtitle>
                      {step.subtitle || DEFAULT_TEXTCOLLECTION_SUBTITLE}
                    </StepCard.Subtitle>
                  </StepCard.HeaderColumn>
                </StepCard.HeaderLeft>
                <StepCard.HeaderRight>
                  {showTwoCandidateDiffResult && (
                    <StepCard.Button
                      icon="file-plus-minus"
                      selected={tab === TextCollectionPerResponseTab.HideDiff}
                      tooltip={
                        tab === TextCollectionPerResponseTab.ShowDiff ? 'Hide Diff' : 'Show Diff'
                      }
                      onClick={() =>
                        setTab(
                          tab === TextCollectionPerResponseTab.ShowDiff
                            ? TextCollectionPerResponseTab.HideDiff
                            : TextCollectionPerResponseTab.ShowDiff,
                        )
                      }
                    />
                  )}
                  {!taskContext.enableUnifiedFeedback && (
                    <CommentButton
                      visible={true}
                      childId={constructStepChildId(step)}
                      turnIndex={step.turn}
                    />
                  )}
                  <PinButton responseIndex={responseIndex} />
                </StepCard.HeaderRight>
              </StepCard.Header>
              <StepCard.Body className="p-0">
                {rubric && (
                  <RubricCriteriasFieldSection
                    field={rubric}
                    allResponses={allResponses}
                    setAllResponses={setAllResponses}
                    handleSubmit={handleSubmit}
                    candidates={candidates}
                    readOnly={readOnly}
                    setDoesRubricNeedSaving={setDoesRubricNeedSaving}
                    responseIndex={responseIndex}
                  />
                )}
                {showTwoCandidateDiffResult && tab === TextCollectionPerResponseTab.ShowDiff && (
                  <TwoTextSxSDiffView
                    className="md:max-w-screen-lg lg:max-w-screen-xl h-[500px]"
                    textOne={candidates[0]?.message?.content}
                    textTwo={candidates[1]?.message?.content}
                    textOneTitle="Response 1"
                    textTwoTitle="Response 2"
                    variant="compact"
                  />
                )}
                {candidates.map((candidate, index) => (
                  <CandidateTextCollectionCard
                    key={candidate.id}
                    name={`Response ${index + 1}`}
                    step={step}
                    // as of writing this comment, only GeminiToolUseTrajectoryStrategy will return a shortResponse in the messageMetadata
                    candidatePreviewText={candidate.message.messageMetadata?.shortResponse}
                    candidateText={candidate.message.content}
                    candidateId={candidate.id}
                    feedback={candidate.message.messageMetadata?.feedback}
                    setCandidateCompletedState={setCandidateCompletedState}
                    response={allResponses[candidate.id]}
                    onResponseSubmit={async response => {
                      const newResponses = produce(allResponses, draft => {
                        draft[candidate.id] = response;
                      });

                      const allAnnotationsComplete = areAllAnnotationsComplete(
                        newResponses,
                        candidates.length,
                      );

                      if (allAnnotationsComplete) {
                        const isValid = await handleSubmit(newResponses);
                        if (isValid) {
                          setAllResponses(newResponses);
                          setSavedReactUIGeneratedUrlWithMetadataByModel(
                            reactUIGeneratedUrlWithMetadataByModel,
                          );
                        }
                      } else {
                        setAllResponses(newResponses);
                      }
                    }}
                    hasSubmittedResponse={!!response}
                    readOnly={readOnly}
                    lastPrompt={lastPrompt}
                    lastResponse={lastResponse}
                    chatTaskBehaviorLogMetadata={{
                      responseIndex,
                      suffix: `candidate-${index}`,
                      candidateId: candidate.id,
                    }}
                    childId={constructStepChildId(step, candidate.id)}
                    enableInlineComments={!!step.params.enable_inline_comments}
                    turnIndex={step.turn}
                    attachments={candidate?.attachments}
                    responseTemplatingProps={{
                      showResponseTemplatingIcon: !!taskContext.responseTemplatingMode,
                      childId: constructStepChildId(step, candidate.id),
                      metadata: {
                        stepIndex: responseIndex,
                        candidateId: candidate.id,
                      },
                    }}
                    enableIndividualModelResponseReroll={
                      enableIndividualModelResponseReroll && isSkipSelectionEnabled
                    }
                    previousModelResponseSelectorStepId={modelResponseSelectorStepId}
                    savedReactUIGeneratedUrlWithMetadata={
                      savedReactUIGeneratedUrlWithMetadataByModel?.[candidate.id]
                    }
                    reactUIGeneratedUrlWithMetadata={
                      reactUIGeneratedUrlWithMetadataByModel?.[candidate.id]
                    }
                    setReactUIGeneratedUrlWithMetadata={reactUIGeneratedUrlWithMetadata => {
                      handleUpdateReactGeneratedUrlWithMetadataByModel(
                        candidate.id,
                        reactUIGeneratedUrlWithMetadata,
                      );
                    }}
                  />
                ))}

                {rubric && (
                  <RubricSummaryTable
                    candidates={candidates}
                    allResponses={allResponses}
                    rubricField={rubric}
                    responseScoreField={responseScoreField}
                  />
                )}

                {error && !complete && (
                  <div className="px-4 mt-2">
                    <Alert variant="danger">
                      <Typography.Paragraph2>{error}</Typography.Paragraph2>
                    </Alert>
                  </div>
                )}
              </StepCard.Body>
            </StepCard>
          </>
        )}
      </TextCollectionPerResponseTabsWrapper>
    </StepWidthWrapper>
  );
};

export default memo(TextCollectionPerResponseTaskComponent);
